import { useEffect } from 'react';
import {
  OnboardingTasksResponseDto,
  OnboardingTasksStatusType,
  OnboardingType,
} from '@tiendanube/common';
import { useStorage } from 'commons/hooks';
import {
  trackingOnboardingPromoteFinished,
  trackingOnboardingPromoteShow,
  trackingOnboardingSetupFinished,
  trackingOnboardingSetupShow,
} from 'domains/Dashboard/tracking';

interface UseOnboardingTrackingParams {
  storeId: string;
  onboardingType: OnboardingType;
  onboardingTasks: OnboardingTasksResponseDto | null | undefined;
  isSuccessOnboardingTasks: boolean;
  onboardingStatus: OnboardingTasksStatusType;
  showPromoteTasks: boolean | undefined;
}

/**
 * Custom hook to handle onboarding funnel tracking
 * Tracks: setup show, setup finished, promote show, promote finished
 */
export function useOnboardingTracking({
  storeId,
  onboardingType,
  onboardingTasks,
  isSuccessOnboardingTasks,
  onboardingStatus,
  showPromoteTasks,
}: UseOnboardingTrackingParams): void {
  // Local storage flags to ensure one-time tracking per store
  const [setupShowLogged, setSetupShowLogged, setupShowLoaded] =
    useStorage<boolean>(`onboarding_setup_show_logged-${storeId}`, false);
  const [setupFinishedLogged, setSetupFinishedLogged, setupFinishedLoaded] =
    useStorage<boolean>(`onboarding_setup_finished_logged-${storeId}`, false);
  const [promoteShowLogged, setPromoteShowLogged, promoteShowLoaded] =
    useStorage<boolean>(`onboarding_promote_show_logged-${storeId}`, false);
  const [
    promoteFinishedLogged,
    setPromoteFinishedLogged,
    promoteFinishedLoaded,
  ] = useStorage<boolean>(
    `onboarding_promote_finished_logged-${storeId}`,
    false,
  );

  // Track first time setup section is shown
  useEffect(() => {
    if (
      isSuccessOnboardingTasks &&
      onboardingStatus === 'pending' &&
      setupShowLoaded &&
      !setupShowLogged
    ) {
      trackingOnboardingSetupShow(onboardingType);
      setSetupShowLogged(true);
    }
  }, [
    isSuccessOnboardingTasks,
    onboardingStatus,
    setupShowLoaded,
    setupShowLogged,
    onboardingType,
    setSetupShowLogged,
  ]);

  // Track setup finished once
  useEffect(() => {
    if (
      isSuccessOnboardingTasks &&
      onboardingStatus === 'completed' &&
      setupFinishedLoaded &&
      !setupFinishedLogged
    ) {
      trackingOnboardingSetupFinished(onboardingType);
      setSetupFinishedLogged(true);
    }
  }, [
    isSuccessOnboardingTasks,
    onboardingStatus,
    setupFinishedLoaded,
    setupFinishedLogged,
    onboardingType,
    setSetupFinishedLogged,
  ]);

  // Track first time promote tasks are shown
  useEffect(() => {
    const promoteGroupsShown =
      onboardingTasks?.promoteGroupedTasks?.filter(
        (group) => group.tasks && group.tasks.length > 0,
      ).length ?? 0;

    const statusForPayload =
      onboardingStatus === 'pending' ? undefined : onboardingStatus;

    if (
      isSuccessOnboardingTasks &&
      showPromoteTasks &&
      promoteShowLoaded &&
      !promoteShowLogged
    ) {
      trackingOnboardingPromoteShow(onboardingType, {
        onboarding_status: statusForPayload,
        promote_groups_shown: promoteGroupsShown,
      });
      setPromoteShowLogged(true);
    }
  }, [
    isSuccessOnboardingTasks,
    showPromoteTasks,
    promoteShowLoaded,
    promoteShowLogged,
    onboardingType,
    setPromoteShowLogged,
    onboardingTasks?.promoteGroupedTasks,
    onboardingStatus,
  ]);

  // Track promote finished once (all promote groups empty)
  useEffect(() => {
    const groups = onboardingTasks?.promoteGroupedTasks ?? [];
    const hasGroups = groups.length > 0;
    const allEmpty =
      hasGroups && groups.every((g) => (g.tasks ?? []).length === 0);

    if (
      isSuccessOnboardingTasks &&
      allEmpty &&
      promoteFinishedLoaded &&
      !promoteFinishedLogged
    ) {
      trackingOnboardingPromoteFinished(onboardingType);
      setPromoteFinishedLogged(true);
    }
  }, [
    isSuccessOnboardingTasks,
    onboardingTasks?.promoteGroupedTasks,
    promoteFinishedLoaded,
    promoteFinishedLogged,
    onboardingType,
    setPromoteFinishedLogged,
  ]);
}
